import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Spin, Timeline } from 'antd-v5';
import moment from 'moment';
import { getRequest } from '../../../../core/apiRequests';
import { getClientPurchases } from '../../../../services/clients';
import { dateNumberToMoment } from '../../../../utils/dateNumberToMoment';
import {
  PurchaseOutline,
  ClientActivity as ClientActivityIcon,
  Message as MessageIcon,
} from '../../../../componentsV2/Layout/Report/components/Icons';
import TimelineItem from '../../../../componentsV2/Layout/Report/components/Timeline';
import SalesTimelineItem from '../../../../componentsV2/Layout/Report/components/SalesTimelineItem';
import useVendorByKey from '../../../../hooks/query/useVendorByKey';

const InquiryExpandedRow = ({ inquiry, auth }) => {
  const history = useHistory();
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(false);

  const clientName = inquiry?.clientName || '';

  useEffect(() => {
    if (!inquiry?.clientId || !auth?.merchantId) {
      setTimelineData([]);
      return;
    }

    setLoading(true);

    const activityPromise = getRequest({
      loadingStatusOff: true,
      params: ['clients', auth.merchantId, inquiry.clientId, 'getClientRecentActivity'],
      query: { limit: 10 },
    }).catch(() => null);

    const purchasesPromise = getClientPurchases({
      clientId: inquiry.clientId,
      loadingStatusOff: true,
    }).catch(() => null);

    Promise.all([activityPromise, purchasesPromise])
      .then(([activityData, purchasesData]) => {
        // Normalize activities
        const activities = (activityData?.recentActivity || []).map((item, idx) => {
          const isMessage = item.type?.name === 'SMS' || item.type?.name === 'MMS';
          return {
            id: `activity-${idx}`,
            itemType: isMessage ? 'message' : 'activity',
            actor: item.createdBy?.name || 'Unknown',
            action: item.type?.name || 'performed an action',
            date: item.createdDate,
            detail: item.notes || '',
            sortDate: item.createdDate ? moment(item.createdDate).valueOf() : 0,
          };
        });

        // Normalize purchases
        const purchases = Array.isArray(purchasesData) ? purchasesData : purchasesData?.data || [];
        const purchaseItems = purchases.map((p, idx) => {
          const price = parseFloat(p.unit_price || p.unitPrice || 0);
          const qty = parseInt(p.unit_quantity || p.quantity || 1, 10);
          const amount = price * qty;
          const txDate = p.transaction_date_time || p.transactionDateTime;
          const parsedDate = txDate ? dateNumberToMoment(txDate) : null;
          return {
            id: `purchase-${idx}`,
            itemType: 'purchase',
            amount,
            description: p.description || p.product_name || p.productName || '',
            date: parsedDate ? parsedDate.toISOString() : txDate,
            sortDate: parsedDate ? parsedDate.valueOf() : 0,
          };
        });

        // Merge and sort by date descending
        const combined = [...activities, ...purchaseItems].sort((a, b) => b.sortDate - a.sortDate);
        // Limit to 10 most recent items
        setTimelineData(combined.slice(0, 10));
      })
      .finally(() => setLoading(false));
  }, [inquiry?.clientId, auth?.merchantId]);

  const timelineItems = useMemo(
    () =>
      timelineData.map(item => {
        if (item.itemType === 'purchase') {
          return {
            key: item.id,
            dot: <PurchaseOutline style={{ color: '#389E0D', fontSize: 18 }} />,
            children: (
              <SalesTimelineItem
                client_name={clientName}
                sales_transaction_amount={item.amount}
                description={item.description}
                activity_date={item.date}
              />
            ),
          };
        }

        const icon =
          item.itemType === 'message' ? (
            <MessageIcon style={{ fontSize: 14, color: '#1677ff' }} />
          ) : (
            <ClientActivityIcon style={{ fontSize: 14, color: '#722ED1' }} />
          );

        const title = (
          <>
            <span className="fw-600">{item.actor}</span>
            {` ${item.action} on `}
            <span className="fw-600">{item.date ? moment(item.date).format('MMM DD, YYYY') : ''}</span>
          </>
        );

        return {
          key: item.id,
          dot: icon,
          children: <TimelineItem title={title} description={item.detail} />,
        };
      }),
    [timelineData, clientName],
  );

  return (
    <div className="inquiry-expanded-row" data-testid="inquiries__table--expanded-row">
      <div className="inquiry-expanded-row__col">
        {loading ? (
          <Spin size="small" />
        ) : (
          <div data-testid="inquiries__expanded-row--timeline">
            <Timeline items={timelineItems} />
          </div>
        )}
      </div>
      <div className="inquiry-expanded-row__col">
        <div className="inquiry-expanded-row__field">
          <span className="inquiry-expanded-row__label">STORY</span>
          <span className="inquiry-expanded-row__value" style={{ whiteSpace: 'pre-wrap' }}>
            {inquiry?.story || 'No story provided'}
          </span>
        </div>
        {inquiry?.requestedProducts?.length > 0 && (
          <div className="inquiry-expanded-row__field">
            <span className="inquiry-expanded-row__label">MODELS</span>
            <div className="inquiry-expanded-row__models">
              {inquiry.requestedProducts.map(p => (
                <div key={p.vendorItemId || p.mpn} className="inquiry-expanded-row__model-item">
                  {p.image && <img className="inquiry-expanded-row__model-image" src={p.image} alt={p.mpn} />}
                  <div>
                    {p.vendorItemId && rolexVendorId ? (
                      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                      <span
                        className="inquiry-expanded-row__model-name inquiry-expanded-row__model-name--link"
                        onClick={() => history.push(`/Products?vendorId=${rolexVendorId}&productId=${p.vendorItemId}`)}
                      >
                        {p.collectionName}
                      </span>
                    ) : (
                      <span className="inquiry-expanded-row__model-name">{p.collectionName}</span>
                    )}
                    <span className="inquiry-expanded-row__model-mpn">{p.mpn}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="inquiry-expanded-row__col">
        <div className="inquiry-expanded-row__field">
          <span className="inquiry-expanded-row__label">TAGS</span>
          <div className="inquiry-expanded-row__tags">
            {(inquiry?.tags || []).map(tag => (
              <span
                key={tag.name || tag}
                className="inquiry-tag-pill"
                style={{ backgroundColor: tag.color || '#818181' }}
              >
                {tag.name || tag}
              </span>
            ))}
          </div>
        </div>
        <div className="inquiry-expanded-row__field">
          <span className="inquiry-expanded-row__label">CREATED BY</span>
          <span className="inquiry-expanded-row__value">{inquiry?.createdByName}</span>
        </div>
        <div className="inquiry-expanded-row__field">
          <span className="inquiry-expanded-row__label">ASSIGNED TO</span>
          <span className="inquiry-expanded-row__value">{inquiry?.associateName}</span>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(InquiryExpandedRow);
